<template>
    <div data-testid="materiality-reports" class="all-height all-width">
        <div class="pa-6 pb-16 d-flex flex-column gap-6">

            <ReportHeader 
            title="Materiality Reports" 
            test-id-prefix="materiality-reports"
            @refresh="fetchInitialData" />

            <div class="d-flex flex-column gap-6">
                <ReportFilter 
                apply-button-text="Generate" 
                cycletype="Actual" 
                :showyear="false" 
                :showperiod="true"
                :showrutype="false" 
                :showSync="false" 
                :showFavourite="false" 
                :showReset="true" 
                :showApply="true"
                :isLoading="filterLoading" 
                :showrole="false" 
                :showreportingunit="true" 
                :shownetwork="true"
                :showregion="true" 
                :showcountry="true" 
                :showmateriality="true" 
                :showcycletype="true"
                :showmultireportingunit="true" 
                :showmultinetwork="true" 
                :showmulticountry="true"
                :showmultiregion="true" 
                :showmultiperiod="true" 
                @newdata="handleFilterData" />
            </div>
            <ReportTable 
            :loading="getMaterialityReportListLoading" 
            :headers="getMaterialityReportHeader"
            :items="formattedMaterialityData" 
            :total-pages="getMaterialityReportTotalPages"
            :current-page="currentPage" 
            :items-per-page="itemsPerPage" 
            :data-slots="dataSlots"
            :shimmer-slots="shimmerSlots"
            test-id-prefix="materiality-reports"
            @page-change="handlePageChange" 
            @download="downloadFile" 
            @view-details="showDetails"
            @filter-change="handleTableFilterChange">
                <template v-slot:indexno="{ item }">
                    <div>
                        {{ item.serial_number }}
                    </div>
                </template>
                <template v-slot:materiality_type="{ item }">
                    <div>{{ getMaterialityTypeLabel(item.materiality_type) }}</div>
                </template>
            </ReportTable>
        </div>

        <ReportDialog v-model="detailsDialog" v-if="detailsDialog && detailsItem" title="Materiality Reports"
            :details-item="detailsItem" test-id-prefix="materiality-report-dialog" :show-ru-type="false">
            <template v-slot:header-fields="{ item }">
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Period</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ item.period_detail.year }} - {{ getMonthName(item.period_detail.number) }}
                    </v-col>
                </v-row>


                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Materiality Type</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ getMaterialityTypeLabel(item.materiality_type) }}
                    </v-col>
                </v-row>
            </template>

            <template v-slot:after-ru-fields="{ item }">
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Cycle Type</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold ">
                        {{ getCycleTypeLabel(item.cycle_type) }}
                    </v-col>
                </v-row>
            </template>
        </ReportDialog>
    </div>
</template>

<script>
import ReportHeader from './components/ReportHeader.vue';
import ReportTable from './components/ReportTable.vue';
import ReportDialog from './components/ReportDialog.vue';
import { STATUS_VARIANTS } from '@/modules/varianceanalysis/utils/statusConstants.js';
import { PAGINATION, REPORTS_API, MATERIALITY_TYPE_LABELS, CYCLE_TYPE_LABELS, MONTHS } from '@/modules/varianceanalysis/utils/constants.js';
import { mapActions, mapGetters } from 'vuex';
import ReportFilter from '../../views/ReportFilter.vue';

export default {
    name: 'MaterialityReports',

    components: {
        ReportFilter,
        ReportHeader,
        ReportTable,
        ReportDialog
    },

    data() {
        return {
            selectedFilter: {},
            cycletype: 'Actual',
            filterLoading: false,
            detailsDialog: false,
            detailsItem: null,
            fetchTimeout: null,
            isInitialLoad: true,
            currentPage: 1,
            itemsPerPage: PAGINATION.DEFAULT_LIMIT,
            statusFilter: [],
            dataSlots: ['indexno', 'materiality_type', 'created_at', 'status', 'action'],
            shimmerSlots: ['indexno', 'materiality_type', 'created_at', 'status', 'action'],
        };
    },

    mounted() {
        // Initial data fetch is deferred to handleFilterData when ReportFilter
        // signals loading complete (key='loading', value=false).
    },

    computed: {
        ...mapGetters('varianceanalysis/reports', [
            'getMaterialityReportData',
            'getMaterialityReportListLoading',
            'getMaterialityReportFilterLoading',
            'getMaterialityReportHeader',
            'getMaterialityReportTotalPages',
        ]),

        /**
         * Generates shimmer/skeleton placeholder items for loading state
         * @returns {Array} Array of placeholder items for skeleton loader display
         */
        shimmerItems() {
            return Array.from({ length: 5 }, (_, index) => ({
                __key: index,
                __formatedkey: index,
                _id: index,
                indexno: index + 1,
                period_detail: '',
                created_at: '',
                status: '',
                action: '',
            }));
        },

        /**
         * Reactively formats materiality report data for display in the data table
         * @returns {Array} Formatted array of materiality report items
         */
        formattedMaterialityData() {
            return this.getMaterialityReportData.map((item, index) => ({
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                serial_number: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                ru_codes: item.ru_codes,
                period: item.period,
                errormessage: item.errormessage,
                stage: item.stage,
                status: this.getStages(item.stage),
                created_at: item.created_at,
                created_by: item.created_by,
                user_detail: item.user_detail,
                country_detail: item.country_details,
                region_detail: item.region_details,
                period_detail: item.period_detail,
                reportingunit_detail: item.reportingunit_details,
                network_detail: item.network_details,
                materiality_type: item.materiality_type,
                cycle_type: item.cycle_type,
            }));
        },
    },


    beforeDestroy() {
        if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
        this.selectedFilter = {};
        this.cycletype = 'Actual';
        this.filterLoading = false;
    },

    methods: {
        ...mapActions('varianceanalysis/reports', [
            'fetchMaterialityReportList',
            'fetchMaterialityReportFiltered',
        ]),

        /**
         * Handles page change from Pagination component - fetches data for new page
         * @param {Number} page - New page number (1-based from Pagination component)
         */
        handlePageChange(page) {
            this.currentPage = page; // Update current page state for UI
            this.fetchPageData(page - 1); // API uses 0-based page index
        },

        /**
         * Handles status filter changes from DataTableNew column filter
         * @param {Object} filters - Filter object e.g. { status: ['ready', 'failed'] }
         */
        handleTableFilterChange(filters) {
            if (this.isInitialLoad) return; // Skip filter events during initial load
            this.statusFilter = filters.status || [];
            this.currentPage = 1;
            this.fetchPageData(0);
        },

        /**
         * Fetches data for a specific page
         * @param {Number} page - Page number to fetch
         */
        async fetchPageData(page) {
            try {
                await this.fetchMaterialityReportList({
                    page: page,
                    limit: this.itemsPerPage,
                    cycletype: this.cycletype,
                    type: 'allrumateriality_' + this.cycletype.toLowerCase(),
                    ...(this.statusFilter.length ? { status: this.statusFilter } : {})
                });
            } catch (error) {
                console.error('[MaterialityReports]: Error fetching page data', error);
            }
        },

        /**
         * Gets month name from month number
         * @param {Number} monthNumber - Month number (1-12)
         * @returns {String} Month name
         */
        getMonthName(monthNumber) {
            if (!monthNumber) return '';
            return MONTHS[monthNumber - 1] || monthNumber;
        },

        /**
         * Downloads report file by ID
         * @param {String} id - Report ID
         */
        downloadFile(id) {
            this.$nova.downloadFile(undefined, {}, REPORTS_API.DOWNLOAD + id);
        },

        /**
         * Shows details dialog for a report item
         * @param {Object} item - Report item
         */
        showDetails(item) {
            this.detailsDialog = true;
            this.detailsItem = item;
        },

        /**
         * Maps stage codes to their corresponding status variant constants
         * @param {Number} stage - The stage code
         * @returns {String} The stage status variant constant
         */
        getStages(stage) {
            switch (stage) {
                case 0:
                    return STATUS_VARIANTS.GENERATING;
                case 1:
                    return STATUS_VARIANTS.READY;
                case 4:
                    return STATUS_VARIANTS.GENERATING;
                case 2:
                    return STATUS_VARIANTS.FAILED;
                default:
                    return STATUS_VARIANTS.FAILED;
            }
        },

        /**
         * Handles filter data changes from GlobalFilter component
         * @param {Object} filterData - Filter data with key-value pairs
         */
        handleFilterData(filterData) {
            if (filterData.key && filterData.value !== undefined) {
                this.$set(this.selectedFilter, filterData.key, filterData.value);
            }

            if (filterData.key === 'loading' && filterData.value === false && this.isInitialLoad) {
                this.isInitialLoad = false;
                this.fetchInitialData();
                return;
            }

            if (filterData.reload) {
                if (this.fetchTimeout) clearTimeout(this.fetchTimeout);

                this.fetchTimeout = setTimeout(() => {
                    this.fetchFilteredData();
                }, 100);
            }
        },

        /**
         * Fetches initial paginated data on component mount
         */
        async fetchInitialData() {
            try {
                this.currentPage = 1; // Reset to first page (1-based for UI)
                this.statusFilter = []; // Reset status filter
                await this.fetchMaterialityReportList({
                    page: 0,
                    limit: this.itemsPerPage,
                    cycletype: this.cycletype,
                    type: 'allrumateriality_' + this.cycletype.toLowerCase()
                });
            } catch (error) {
                console.error('[MaterialityReports]: Error fetching initial data', error);
            }
        },

        /**
         * Fetches filtered materiality report data
         */
        async fetchFilteredData() {
            console.log('[MaterialityReports]: Selected filters', this.selectedFilter);

            try {
                this.filterLoading = true;
                this.currentPage = 1; // Reset to first page on filter apply
                const payload = {
                    period: this.transformToArray(this.selectedFilter.selectedperiod),
                    network: this.transformToArray(this.selectedFilter.selectednetwork),
                    country: this.transformToArray(this.selectedFilter.selectedcountry),
                    region: this.transformToArray(this.selectedFilter.selectedregion),
                    reportingunit: this.transformReportingUnit(this.selectedFilter.selectedreportingunit),
                    type: 'allrumateriality_' + this.cycletype.toLowerCase(),
                    cycletype: this.transformFilterValue(this.selectedFilter.selectedcycletype) || this.cycletype,
                    materiality_type: this.transformFilterValue(this.selectedFilter.selectedmateriality) || 'ru',
                };

                await this.fetchMaterialityReportFiltered(payload);
                await this.fetchInitialData();
            } catch (error) {
                console.error('[MaterialityReports]: Error fetching filtered data', error);
            } finally {
                this.filterLoading = false;
            }
        },

        /**
         * Transforms filter value to array: returns "all" if empty/null, otherwise returns array
         * @param {String|Array|null} value - Filter value
         * @returns {String|Array} Transformed value as array or 'all'
         */
        transformToArray(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        /**
         * Transforms reporting unit value: returns "all" if empty or 'all' string, otherwise returns array
         * @param {String|Array|null} value - Reporting unit value
         * @returns {String|Array} Transformed value
         */
        transformReportingUnit(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        /**
         * Transforms filter value: returns "all" if empty/null, otherwise returns the value
         * @param {String|null} value - Filter value
         * @returns {String} Transformed value
         */
        transformFilterValue(value) {
            return value || 'all';
        },

        /**
         * Gets the display label for a materiality type
         * @param {String} type - Materiality type code (sl, ru, gl)
         * @returns {String} Display label
         */
        getMaterialityTypeLabel(type) {
            return MATERIALITY_TYPE_LABELS[type] || type;
        },

        /**
         * Gets the display label for a cycle type
         * @param {String} type - Cycle type code (actual, forecast)
         * @returns {String} Display label
         */
        getCycleTypeLabel(type) {
            return CYCLE_TYPE_LABELS[type] || type;
        },
    },
};
</script>

<style scoped>
.failed-reason {
    color: var(--status-red);
}
</style>
